import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Download, Save, X, Pencil } from "lucide-react";

const EditableCell = ({ data, metodo, value, details, disableEdit = false, bgClass = "", editingCell, editingValue, onEditingValueChange, onStartEdit, onSaveEdit, onCancelEdit }) => {
  const isEditing = editingCell?.data === data && editingCell?.metodo === metodo;
  
  if (isEditing) {
    return (
      <td className={`p-1 ${bgClass}`}>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            step="0.01"
            value={editingValue}
            onChange={(e) => onEditingValueChange(e.target.value)}
            className="h-7 w-20 text-right text-sm bg-muted border-white/20 text-foreground"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSaveEdit();
              if (e.key === 'Escape') onCancelEdit();
            }}
          />
          <Button size="icon" variant="ghost" onClick={onSaveEdit} className="h-6 w-6">
            <Save className="h-3 w-3 text-lime-400" />
          </Button>
          <Button size="icon" variant="ghost" onClick={onCancelEdit} className="h-6 w-6">
            <X className="h-3 w-3 text-muted-foreground" />
          </Button>
        </div>
      </td>
    );
  }
  
  return (
    <td 
      className={`text-right text-sm transition-colors p-2 ${disableEdit ? "" : "cursor-pointer hover:bg-lime-200"} ${bgClass}`}
      onClick={() => !disableEdit && onStartEdit(data, metodo, value)}
      title={disableEdit ? "Il dettaglio POS per banca è protetto" : "Modifica importo"}
      data-testid={`registro-${metodo.toLowerCase()}-${data}`}
    >
      <div>€ {value.toFixed(2)}</div>
      {details && Object.entries(details).map(([nome, importo]) => (
        <div key={nome} className="text-[10px] leading-4 font-normal text-blue-700 whitespace-nowrap">
          {nome}: € {Number(importo).toFixed(2)}
        </div>
      ))}
    </td>
  );
};

export const RegistroGiornaliero = ({
  registroPerGiorno,
  deliveryColumns,
  loadingRegistro,
  editingCell,
  editingValue,
  onEditingValueChange,
  onStartEditCell,
  onSaveEditCell,
  onCancelEditCell,
  onEditGiorno,
  onExport,
  formatDateIT
}) => {
  const cellProps = { editingCell, editingValue, onEditingValueChange, onStartEdit: onStartEditCell, onSaveEdit: onSaveEditCell, onCancelEdit: onCancelEditCell };

  return (
    <div className="bg-card backdrop-blur-sm border border-border rounded-2xl overflow-hidden">
      <div className="p-4 flex flex-row items-center justify-between border-b border-border">
        <div>
          <h3 className="text-base font-semibold text-lime-400">Registro Giornaliero</h3>
          <p className="text-xs text-muted-foreground mt-1">Clicca su una cella per modificare</p>
        </div>
        <Button variant="outline" size="sm" onClick={onExport} className="border-border text-foreground hover:bg-accent hover:text-accent-foreground">
          <Download className="h-4 w-4 mr-1" />
          Excel
        </Button>
      </div>
      <div className="p-0">
        {loadingRegistro ? (
          <div className="p-8 text-center text-muted-foreground">Caricamento...</div>
        ) : registroPerGiorno.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">Nessun dato nel periodo</div>
        ) : (
          <div className="overflow-auto max-h-[calc(100vh-320px)] border border-border rounded-xl">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-30">
                <tr className="bg-muted border-b border-border">
                  <th rowSpan={2} className="font-bold text-center border-r border-border w-24 p-2 bg-muted text-foreground">DATA</th>
                  <th colSpan={2 + deliveryColumns.length + 2} className="font-bold text-center bg-lime-200 border-r border-border text-lime-700 p-2">ENTRATE</th>
                  <th colSpan={2} className="font-bold text-center bg-red-200 border-r border-border text-red-700 p-2">USCITE</th>
                  <th rowSpan={2} className="font-bold text-center bg-amber-200 border-r border-border text-amber-700 p-2 text-xs">VERS.<br/>BANCA</th>
                  <th colSpan={3} className="font-bold text-center bg-blue-200 text-blue-700 p-2">TOTALI</th>
                  <th rowSpan={2} className="w-12 bg-muted"></th>
                </tr>
                <tr className="bg-white border-b border-border">
                  <th className="text-center text-xs bg-lime-100 text-foreground/80 font-medium p-2">Contanti</th>
                  <th className="text-center text-xs bg-lime-100 text-foreground/80 font-medium p-2">POS</th>
                  {deliveryColumns.map(col => (
                    <th key={col} className="text-center text-xs bg-lime-100 text-foreground/80 font-medium p-2">{col}</th>
                  ))}
                  <th className="text-center text-xs bg-lime-200 font-semibold text-lime-700 p-2">LORDE</th>
                  <th className="text-center text-xs bg-lime-200 font-semibold border-r border-border text-lime-700 p-2">NETTE</th>
                  <th className="text-center text-xs bg-red-100 text-foreground/80 font-medium p-2">Contanti</th>
                  <th className="text-center text-xs bg-red-200 font-semibold border-r border-border text-red-700 p-2">Totali</th>
                  <th className="text-center text-xs bg-blue-100 text-foreground/80 font-medium p-2">F.Cassa Iniz.</th>
                  <th className="text-center text-xs bg-blue-100 text-foreground/80 font-medium p-2">F.Cassa Fin.</th>
                  <th className="text-center text-xs bg-blue-200 font-semibold text-blue-700 p-2">TOT. NETTO</th>
                </tr>
              </thead>
              <tbody>
                {registroPerGiorno.map((row) => (
                  <tr key={row.data} className="hover:bg-gray-50 border-b border-gray-100" data-testid={`registro-row-${row.data}`}>
                    <td className="font-mono text-sm text-center border-r border-border font-medium p-2 bg-white text-foreground">{formatDateIT(row.data, true)}</td>
                    <EditableCell data={row.data} metodo="Contanti" value={row.contanti} bgClass="bg-lime-50 text-foreground" {...cellProps} />
                    <EditableCell data={row.data} metodo="POS" value={row.pos} details={row.hasDetailedPos ? row.posBreakdown : null} disableEdit={row.hasDetailedPos} bgClass="bg-lime-50 text-foreground" {...cellProps} />
                    {deliveryColumns.map(col => <EditableCell key={col} data={row.data} metodo={col} value={row.deliveryTotals[col] || 0} bgClass="bg-lime-50 text-foreground" {...cellProps} />)}
                    <td className="text-right text-sm font-semibold bg-lime-100 text-lime-600 p-2" data-testid={`registro-lorde-${row.data}`}>€ {row.entrateLorde.toFixed(2)}</td>
                    <td className="text-right text-sm font-semibold bg-lime-100 border-r border-border text-lime-600 p-2" data-testid={`registro-nette-${row.data}`}>€ {row.entrateNette.toFixed(2)}</td>
                    <td className="text-right text-sm bg-red-50 text-foreground p-2">€ {row.usciteContanti.toFixed(2)}</td>
                    <td className="text-right text-sm font-semibold bg-red-100 border-r border-border text-red-600 p-2">€ {row.usciteTotali.toFixed(2)}</td>
                    <td className="text-right text-sm bg-amber-50 border-r border-border p-2" style={{ color: row.versamentiBanca > 0 ? '#b45309' : '#999' }}>
                      {row.versamentiBanca > 0 ? `€ ${row.versamentiBanca.toFixed(2)}` : '-'}
                    </td>
                    <td className="text-right text-sm bg-blue-50 text-foreground p-2">€ {row.fondoCassaIniziale.toFixed(2)}</td>
                    <td className="text-right text-sm bg-blue-50 text-foreground p-2">€ {row.fondoCassaFinale.toFixed(2)}</td>
                    <td className="text-right text-sm font-bold bg-blue-100 text-blue-600 p-2">€ {row.totaleNetto.toFixed(2)}</td>
                    <td className="text-center bg-white p-1">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => onEditGiorno(row.data)}
                        className="h-7 w-7 hover:bg-muted"
                        title="Modifica date entrate"
                      >
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
